/**
 * RevisionTimeline Component
 *
 * Timeline display for revision history
 */

import React from 'react';
import { CheckCircle as MatchIcon, Warning as MismatchIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { TRIGGER_LABELS } from '../../constants';
import type { AuditRevisionSummary, AuditTrigger } from '../../types';
import { DeltaBadge } from '../shared/DeltaBadge';
import { QualityScore } from '../shared/QualityScore';

interface RevisionTimelineProps {
  /** List of revisions (should be sorted newest first) */
  revisions: AuditRevisionSummary[];
  /** Currently selected revision */
  selectedRevision?: number;
  /** Callback when clicking a revision */
  onSelectRevision?: (revision: number) => void;
}

/**
 * Format datetime to short readable string
 */
const formatTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

interface TimelineItemProps {
  revision: AuditRevisionSummary;
  isFirst: boolean;
  isLast: boolean;
  isSelected: boolean;
  onClick?: () => void;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  revision,
  isFirst,
  isLast,
  isSelected,
  onClick,
}) => {
  const theme = useTheme();

  const trigger = revision.trigger as AuditTrigger;
  const triggerLabel = TRIGGER_LABELS[trigger] || revision.trigger;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick
          ? {
              '& .timeline-content': {
                bgcolor: theme.palette.grey[50],
              },
            }
          : {},
      }}
    >
      {/* Timeline Line */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 20,
        }}
      >
        {/* Top connector */}
        <Box
          sx={{
            width: 2,
            height: 12,
            bgcolor: isFirst ? 'transparent' : theme.palette.grey[300],
          }}
        />
        {/* Dot */}
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isSelected ? theme.palette.primary.main : theme.palette.grey[400],
            border: `2px solid ${isSelected ? theme.palette.primary.light : theme.palette.grey[300]}`,
            flexShrink: 0,
          }}
        />
        {/* Bottom connector */}
        <Box
          sx={{
            width: 2,
            flex: 1,
            bgcolor: isLast ? 'transparent' : theme.palette.grey[300],
          }}
        />
      </Box>

      {/* Content */}
      <Box
        className="timeline-content"
        sx={{
          flex: 1,
          pb: 2,
          mb: isLast ? 0 : 1,
          p: 1.5,
          borderRadius: 1.5,
          border: isSelected ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
          transition: 'background-color 0.15s, border-color 0.15s',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Revision {revision.revision}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {formatTime(revision.created_at)}
          </Typography>
        </Box>

        {/* Trigger */}
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, display: 'block', mb: 1 }}
        >
          {triggerLabel}
        </Typography>

        {/* Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {/* Quality */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <QualityScore score={revision.overall_quality_score} variant="badge" size="small" />
            {revision.quality_delta !== null && revision.quality_delta !== undefined && (
              <DeltaBadge delta={revision.quality_delta} size="small" />
            )}
          </Box>

          {/* CPT Match */}
          {revision.recommended_cpt_codes && revision.recommended_cpt_codes.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexWrap: 'wrap' }}>
              {revision.cpt_match ? (
                <MatchIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
              ) : (
                <MismatchIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />
              )}
              {revision.recommended_cpt_codes.map((code, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: revision.cpt_match
                      ? theme.palette.success.main
                      : theme.palette.warning.main,
                  }}
                >
                  {code}
                  {index < (revision.recommended_cpt_codes?.length ?? 0) - 1 && ', '}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const RevisionTimeline: React.FC<RevisionTimelineProps> = ({
  revisions,
  selectedRevision,
  onSelectRevision,
}) => {
  const theme = useTheme();

  if (revisions.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="body2">No revision history available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {revisions.map((revision, index) => (
        <TimelineItem
          key={revision.revision}
          revision={revision}
          isFirst={index === 0}
          isLast={index === revisions.length - 1}
          isSelected={selectedRevision === revision.revision}
          onClick={onSelectRevision ? () => onSelectRevision(revision.revision) : undefined}
        />
      ))}
    </Box>
  );
};

export default RevisionTimeline;
